import React, {Fragment, useEffect, useState} from "react";
import {Box} from "@mui/material";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import {useTranslation} from "react-i18next";
import {connect, useSelector} from "react-redux";
import {v4 as uuidv4} from "uuid";
import Call from "../../../hocs/call";
import FullscreenProvider, {useFullscreen} from "../../../hocs/fullscreen-provider";
import AlertDialog from "../../alert-dialog";
import SingleViewerPlusFooter from "./Footer";
import SingleViewerPlusHeader from "./Header";
import SingleViewerPlusSidebar from "./Sidebar";
import SingleViewerPlusViewer from "./Viewer";
import {
  backupDatasetSVPState,
  fetchDatasetSVPCodelists,
  hideDatasetSVPDownloadWarning,
  hideDatasetSVPUnavailableViewWarning
} from "../../../state/dataset/single-viewer-plus/actions";
import {themeConfigShowStatViewerFooter} from "../../../state/theme-config/themeConfigSelectors";
import {getDatasetStorageKey} from "../../../utils/other";

const $ = window.jQuery;

const mapStateToProps = ({dataset}) => ({
  datasetUuid: dataset.singleViewerPlus.datasetUuid,
  dimensions: dataset.singleViewerPlus.dimensions,
  codelistTrees: dataset.singleViewerPlus.codelistTrees,
  isDownloadWarningVisible: dataset.singleViewerPlus.isDownloadWarningVisible,
  isUnavailableViewWarningVisible: dataset.singleViewerPlus.isUnavailableViewWarningVisible
});

const mapDispatchToProps = dispatch => ({
  fetchCodelists: ({nodeId, nodeCode, packageId, datasetId, freq, defaultLastNPeriods}) =>
    dispatch(fetchDatasetSVPCodelists(nodeId, nodeCode, packageId, datasetId, freq, defaultLastNPeriods)),
  onDownloadWarningHide: () => dispatch(hideDatasetSVPDownloadWarning()),
  onUnavailableViewHide: () => dispatch(hideDatasetSVPUnavailableViewWarning()),
  onDatasetBackup: datasetUuid => dispatch(backupDatasetSVPState(datasetUuid))
});

const handleStyle = () => {
  const headerHeight = document.getElementById("data-viewer__header")?.offsetHeight || 0;
  const footerHeight = document.getElementById("data-viewer__footer")?.offsetHeight || 0;
  $("#data-viewer__sidebar-viewer-container").height(`calc(100% - ${headerHeight + footerHeight}px)`);

  const sidebarWidth = document.getElementById("data-viewer__sidebar")?.offsetWidth || 0;
  $("#data-viewer__viewer").width(`calc(100% - ${sidebarWidth}px)`);
};

function SingleViewerPlusComponent(props) {
  const {
    nodeId,
    nodeCode,
    categoryPath,
    packageId,
    packageMetadata,
    packageApproved,
    datasetId,
    datasetTitle,
    datasetSource,
    datasetLastUpdate,
    datasetNotes,
    datasetMetadataUrl,
    viewId,
    attachedFiles,
    nodeExtras,

    datasetUuid,
    dimensions,
    codelistTrees,
    isDownloadWarningVisible,
    isUnavailableViewWarningVisible,

    fetchCodelists,
    onDownloadWarningHide,
    onUnavailableViewHide,
    onDatasetBackup
  } = props;

  const {t} = useTranslation();

  const {isFullscreen, handleFullscreen} = useFullscreen();

  const showFooter = useSelector(themeConfigShowStatViewerFooter);

  const [chartId] = useState("chart__" + uuidv4());

  const [mapId] = useState("map__" + uuidv4());

  useEffect(() => {
    return () => {
      const storedDatasets = JSON.parse(sessionStorage.getItem("datasets") || "[]");
      if (datasetUuid && storedDatasets.map(getDatasetStorageKey).includes(datasetUuid)) {
        onDatasetBackup(datasetUuid);
      }
    };
  }, [datasetUuid, onDatasetBackup]);

  useEffect(() => {
    window.addEventListener("resize", handleStyle);
    return () => window.removeEventListener("resize", handleStyle);
  }, []);

  useEffect(() => {
    handleStyle();
  });

  return (
    <Fragment>
      <Box
        id="data-viewer"
        sx={{
          width: "100%",
          height: "100%"
        }}
        className={`data-viewer__single-viewer-plus`}
      >
        <Call
          cb={fetchCodelists}
          cbParam={{
            nodeId: nodeId,
            nodeCode: nodeCode,
            packageId: packageId,
            datasetId: datasetId,
            freq: null,
            defaultLastNPeriods: null
          }}
          disabled={dimensions === null || codelistTrees !== null}
        >
          <Box
            id="data-viewer__header"
            sx={{
              width: "100%",
              padding: isFullscreen ? 0 : "0 16px",
              marginTop: isFullscreen ? "-16px" : 0
            }}
          >
            <SingleViewerPlusHeader
              nodeId={nodeId}
              nodeCode={nodeCode}
              nodeExtras={nodeExtras}
              categoryPath={categoryPath}
              packageId={packageId}
              packageApproved={packageApproved}
              datasetId={datasetId}
              datasetTitle={datasetTitle}
              datasetSource={datasetSource}
              datasetLastUpdate={datasetLastUpdate}
              datasetNotes={datasetNotes}
              viewId={viewId}
              attachedFiles={attachedFiles}
              chartId={chartId}
              mapId={mapId}
              onRender={handleStyle}
              onToggleFullScreen={handleFullscreen}
              isFullscreen={isFullscreen}
            />
          </Box>
          <Box
            id="data-viewer__sidebar-viewer-container"
            sx={{
              display: "flex",
              width: "100%",
              height: "100%",
              minHeight: "400px",
              minWidth: "560px"
            }}
            style={{padding: isFullscreen ? "0" : "0 16px"}}
          >
            <Box id="data-viewer__sidebar" sx={{height: "100%"}} style={{display: isFullscreen ? "none" : "block"}}>
              <SingleViewerPlusSidebar
                nodeId={nodeId}
                nodeCode={nodeCode}
                nodeExtras={nodeExtras}
                packageId={packageId}
                packageMetadata={packageMetadata}
                datasetId={datasetId}
                datasetMetadataUrl={datasetMetadataUrl}
              />
            </Box>
            <Box id="data-viewer__viewer" sx={{height: "100%"}}>
              <SingleViewerPlusViewer
                nodeId={nodeId}
                nodeCode={nodeCode}
                nodeExtras={nodeExtras}
                packageId={packageId}
                datasetId={datasetId}
                datasetTitle={datasetTitle}
                chartId={chartId}
                mapId={mapId}
                isFullscreen={isFullscreen}
              />
            </Box>
          </Box>
          <Box
            id="data-viewer__footer"
            sx={{
              width: "100%",
              overflowX: "auto",
              overflowY: "hidden",
              padding: "8px"
            }}
            style={{display: isFullscreen ? "none" : "block"}}
          >
            {showFooter && (
              <SingleViewerPlusFooter
                dataset={{
                  nodeCode: nodeCode,
                  categoryPath: categoryPath,
                  datasetId: datasetId,
                  datasetTitle: datasetTitle,
                  viewId: viewId
                }}
                onDatasetsChange={handleStyle}
              />
            )}
          </Box>
        </Call>
      </Box>

      <AlertDialog open={isDownloadWarningVisible} onClose={onDownloadWarningHide}>
        <DialogContent>{t("scenes.dataViewer.dialogs.downloadFormat.content")}</DialogContent>
        <DialogActions>
          <Button onClick={onDownloadWarningHide}>{t("commons.confirm.confirm")}</Button>
        </DialogActions>
      </AlertDialog>

      <AlertDialog open={isUnavailableViewWarningVisible} onClose={onUnavailableViewHide}>
        <DialogContent>{t("scenes.dataViewer.dialogs.unavailableView.content")}</DialogContent>
        <DialogActions>
          <Button onClick={onUnavailableViewHide}>{t("commons.confirm.confirm")}</Button>
        </DialogActions>
      </AlertDialog>
    </Fragment>
  );
}

const SingleViewerPlus = props => (
  <FullscreenProvider>
    <SingleViewerPlusComponent {...props} />
  </FullscreenProvider>
);

export default connect(mapStateToProps, mapDispatchToProps)(SingleViewerPlus);
